import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Typography, keyframes } from '@mui/material';
import { GameContext } from '../../../contexts/GameContext';
import { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../contexts/LanguageContext';
import MenuButton from '../../Shared/MenuButton';

const bounce = keyframes`
    0%, 100% { transform: scale(1); }
    20% { transform: scale(1.3) rotate(-5deg); }
    40% { transform: scale(0.95) rotate(3deg); }
    60% { transform: scale(1.15) rotate(-2deg); }
    80% { transform: scale(1.05); }
`;

const fadeInUp = keyframes`
    0% { opacity: 0; transform: translateY(30px); }
    100% { opacity: 1; transform: translateY(0); }
`;

const glow = keyframes`
    0%, 100% { text-shadow: 0 0 20px rgba(193, 145, 255, 0.6), 0 0 40px rgba(193, 145, 255, 0.2); }
    50% { text-shadow: 0 0 30px rgba(193, 145, 255, 0.9), 0 0 60px rgba(193, 145, 255, 0.4), 0 0 80px rgba(255, 200, 100, 0.2); }
`;

const CONFETTI_COLORS = ['#c191ff', '#ff6b9d', '#ffd700', '#00e5ff', '#76ff03', '#ff4081', '#536dfe', '#ff9100'];

function launchConfetti(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d')!;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const pieces: {
        x: number; y: number; w: number; h: number;
        color: string; vx: number; vy: number;
        rotation: number; rotationSpeed: number;
        opacity: number; shape: number;
    }[] = [];

    for (let i = 0; i < 150; i++) {
        pieces.push({
            x: Math.random() * canvas.width,
            y: -20 - Math.random() * canvas.height * 0.5,
            w: 6 + Math.random() * 8,
            h: 4 + Math.random() * 6,
            color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
            vx: (Math.random() - 0.5) * 4,
            vy: 2 + Math.random() * 4,
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.15,
            opacity: 1,
            shape: Math.floor(Math.random() * 3),
        });
    }

    let animId: number;

    function draw() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        let alive = false;

        for (const p of pieces) {
            p.x += p.vx;
            p.vy += 0.04;
            p.y += p.vy;
            p.rotation += p.rotationSpeed;
            p.vx *= 0.999;

            if (p.y > canvas.height + 40) {
                p.opacity -= 0.02;
            }

            if (p.opacity <= 0) continue;
            alive = true;

            ctx.save();
            ctx.globalAlpha = p.opacity;
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rotation);
            ctx.fillStyle = p.color;

            if (p.shape === 0) {
                ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
            } else if (p.shape === 1) {
                ctx.beginPath();
                ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
                ctx.fill();
            } else {
                ctx.beginPath();
                ctx.moveTo(0, -p.h / 2);
                ctx.lineTo(p.w / 2, p.h / 2);
                ctx.lineTo(-p.w / 2, p.h / 2);
                ctx.closePath();
                ctx.fill();
            }

            ctx.restore();
        }

        if (alive) {
            animId = requestAnimationFrame(draw);
        }
    }

    animId = requestAnimationFrame(draw);

    return () => cancelAnimationFrame(animId);
}

const VictoryModal = () => {
    const gameContext = useContext(GameContext);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (!gameContext.gameWinner || !canvasRef.current) return;

        const cleanup = launchConfetti(canvasRef.current);

        const burstInterval = setInterval(() => {
            if (canvasRef.current) launchConfetti(canvasRef.current);
        }, 3500);

        return () => {
            cleanup();
            clearInterval(burstInterval);
        };
    }, [gameContext.gameWinner]);

    const handleBackToHome = async () => {
        await gameContext.getClient().LeaveGame();
        gameContext.setGameWinner(null);
        navigate('/');
    };

    if (!gameContext.gameWinner) return null;

    return (
        <>
            <canvas
                ref={canvasRef}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    pointerEvents: 'none',
                    zIndex: 1500,
                }}
            />
            <Dialog
                open={true}
                disableEscapeKeyDown
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: 'rgba(0, 0, 0, 0.85)' }
                    }
                }}
                sx={{ zIndex: 1400 }}
                PaperProps={{
                    sx: {
                        backgroundColor: '#1a2340',
                        color: 'white',
                        width: 420,
                        maxWidth: '95vw',
                        borderRadius: 3,
                        padding: 3,
                        textAlign: 'center',
                        border: '2px solid rgba(193, 145, 255, 0.4)',
                        boxShadow: '0 0 40px rgba(193, 145, 255, 0.3), 0 0 80px rgba(193, 145, 255, 0.1)',
                        overflow: 'visible',
                        animation: `${fadeInUp} 0.6s ease-out`,
                    }
                }}
            >
                <DialogContent sx={{ px: 2, py: 1 }}>
                    <Typography sx={{
                        fontSize: '5rem',
                        mb: 1,
                        animation: `${bounce} 1.5s ease-in-out infinite`,
                        display: 'inline-block',
                    }}>
                        🏆
                    </Typography>

                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 'bold',
                            mb: 3,
                            lineHeight: 1.3,
                            animation: `${glow} 2s ease-in-out infinite, ${fadeInUp} 0.8s ease-out`,
                        }}
                    >
                        {t('gameOver').replace('{name}', gameContext.gameWinner.name)}
                    </Typography>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        animation: `${fadeInUp} 1s ease-out`,
                    }}>
                        <MenuButton
                            text={t('backToHomeButton')}
                            buttonWidth="auto"
                            textSize="h6"
                            onClick={handleBackToHome}
                            background="url(/images/buttons/menuButton1.png)"
                            hoverBackground="url(/images/buttons/menuButton1_hover.png)"
                            dynamicCursor={false}
                            staticCursorImage=""
                            disabled={false}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default VictoryModal;
